import { and, desc, eq, getTableColumns, isNull, sql } from "drizzle-orm";

import type { DirectWorkspaceInvitation } from "@storyboard/web-client";
import { bulkInsert } from "@/db/bulkInsert";
import { db } from "@/db/db";
import { directWsInvitation, feed, permission, workspaceCommandAlias, workspaceMembership } from "@/db/schema";
import { deduplicateByKeyValue } from "@/models/appSyncFunctions.ts";
import { emptyQuery } from "@/models/emptyQuery.ts";
import { emitTableUpdates } from "@/models/eventing/TableEventManager";
import { logQuery } from "@/models/logQuery.ts";

export async function bulkUpsertDirectWsInvitation(records: DirectWorkspaceInvitation[]) {
  if (records.length === 0) return;
  const deduped = deduplicateByKeyValue(records, (item) => item.id);
  await bulkInsert(directWsInvitation, deduped, {
    onConflict: {
      type: "doUpdate",
      target: [directWsInvitation.id],
      set: {
        name: sql`"excluded"."name"`,
        updatedAt: sql`"excluded"."updatedAt"`,
        email: sql`"excluded"."email"`,
        phoneNumber: sql`"excluded"."phoneNumber"`,
        claimedAt: sql`"excluded"."claimedAt"`,
        claimedBy: sql`"excluded"."claimedBy"`,
        workspaceMembershipId: sql`"excluded"."workspaceMembershipId"`,
      },
    },
  });
  emitTableUpdates(["direct_ws_invitation"]);
}

export function currentFeedPendingInvitesSelect({ currentFeedId }: { currentFeedId?: string }) {
  const query = db
    .select({
      ...getTableColumns(directWsInvitation),
    })
    .from(directWsInvitation)
    .innerJoin(permission, eq(directWsInvitation.workspaceMembershipId, permission.workspaceMembershipId))
    .innerJoin(workspaceMembership, eq(workspaceMembership.id, permission.workspaceMembershipId))
    .where(
      and(
        eq(permission.feedId, currentFeedId),
        eq(permission.name, "read"),
        eq(permission.enabled, true),
        isNull(directWsInvitation.claimedBy),
        eq(workspaceMembership.status, "active"),
      ),
    );

  if (!currentFeedId) {
    return emptyQuery as unknown as typeof query;
  }
  logQuery("Current Feed Pending Invites", query);
  return query;
}

export function workspaceInviteCountResponseSelect({ workspaceId }: { workspaceId?: string }) {
  const query = db
    .select({
      inviteCount: sql`COUNT(${directWsInvitation.id}) as "inviteCount"`,
    })
    .from(directWsInvitation)
    .innerJoin(workspaceMembership, eq(directWsInvitation.workspaceMembershipId, workspaceMembership.id))
    .where(
      and(
        eq(workspaceMembership.workspaceId, workspaceId),
        eq(workspaceMembership.status, "active"),
        isNull(directWsInvitation.claimedBy),
      ),
    );

  if (!workspaceId) {
    return emptyQuery as unknown as typeof query;
  }
  logQuery("Workspace Invite Count", query);
  return query;
}

export const workspaceInvitesAsChannels = (workspaceId: string) => {
  const query = db
    .select({
      ...getTableColumns(directWsInvitation),
      // ...getTableColumns(feed),
      contact: sql`COALESCE(${directWsInvitation.name}, ${directWsInvitation.email}, ${directWsInvitation.phoneNumber}) as contact`,
      date: sql`${directWsInvitation.createdAt} as date`,
      membershipStatus: sql`${workspaceMembership.status} as "membershipStatus"`,
      role: sql`${workspaceMembership.role} as "role"`,
      feedId: workspaceCommandAlias.feedId,
      title: feed.title,
      isAliasFeed: feed.isAliasFeed,
      aliasMembershipId: feed.aliasMembershipId,
      workspaceId: feed.workspaceId,
    })
    .from(directWsInvitation)
    .innerJoin(workspaceMembership, eq(directWsInvitation.workspaceMembershipId, workspaceMembership.id))
    .innerJoin(workspaceCommandAlias, eq(workspaceCommandAlias.workspaceMembershipId, workspaceMembership.id))
    .innerJoin(feed, eq(feed.id, workspaceCommandAlias.feedId))
    .where(
      and(
        eq(workspaceMembership.workspaceId, workspaceId),
        eq(workspaceMembership.status, "active"),
        eq(workspaceMembership.role, "limitedMember"),
        isNull(directWsInvitation.claimedBy),
      ),
    );

  if (!workspaceId) {
    return emptyQuery as unknown as typeof query;
  }
  logQuery("Workspace Invites", query);
  return query;
};

export function workspaceInviteTableSelect({ workspaceId }: { workspaceId?: string }) {
  const query = db
    .select({
      ...getTableColumns(directWsInvitation),
      contact: sql`COALESCE(${directWsInvitation.email}, ${directWsInvitation.phoneNumber}) as contact`,
      date: sql`${directWsInvitation.createdAt} as date`,
      membershipStatus: sql`${workspaceMembership.status} as "membershipStatus"`,
      role: sql`${workspaceMembership.role} as "role"`,
    })
    .from(directWsInvitation)
    .innerJoin(workspaceMembership, eq(directWsInvitation.workspaceMembershipId, workspaceMembership.id))
    .where(
      and(
        eq(workspaceMembership.workspaceId, workspaceId),
        eq(workspaceMembership.status, "active"),
        isNull(directWsInvitation.claimedBy),
      ),
    )
    .orderBy(desc(directWsInvitation.createdAt));

  if (!workspaceId) {
    return emptyQuery as unknown as typeof query;
  }
  logQuery("Workspace Invites", query);
  return query;
}
